import { createContext, useContext } from 'react'
import LoadingSpinner from '@klear/ui/components/spinner'
import type { User } from '@klear/user'
import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { trpc } from '@klear/ui/lib/trpc'
import { isUnauthorizedError, redirectToLogin } from '@klear/ui/lib/auth-redirect'
import { getAuthBaseUrl } from '@klear/runtime-env'
import { ROUTE_LOGIN } from '@klear/constants'

interface AuthContextType {
  user: User
  isLoading: boolean
  error: Error | null
  refetchSession: () => void
  logout: () => Promise<unknown>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const {
    data: userData,
    isLoading,
    error,
    refetch: refetchSession
  } = trpc.auth.getSession.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false
  })

  const user = userData ?? null

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      queryClient.setQueryData(['auth.getSession'], null)
      window.location.href = `${getAuthBaseUrl()}/${ROUTE_LOGIN}`
    },
    onError: (err) => {
      console.error('Logout failed:', err)
    }
  })

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error && !isUnauthorizedError(error)) {
    return <LoadingSpinner />
  }

  if (!user || isUnauthorizedError(error)) {
    redirectToLogin()
    return null
  }

  return (
    <AuthContext.Provider
      value={{
        user: user,
        isLoading,
        error: error as Error | null,
        refetchSession,
        logout: () => logoutMutation.mutateAsync()
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
